import { createUpload } from "@mux/upchunk";
import type { RecordingTarget } from "@msuite/ui/video-recorder";
import { responseHandler } from "@msuite/ui/utils";
import { client } from "@/hono";

/**
 * Ships a finished recording — or a video file the author chose — to the video row that was
 * created for it.
 *
 * The Mux direct upload is opened here rather than at creation time, because it expires an
 * hour after it is minted and a long tutorial would outlive it. Asking twice is safe: the
 * server answers the same URL, and direct uploads are resumable.
 */
export function uploadRecording(videoId: string) {
	return async (blob: Blob, onProgress: (fraction: number) => void) => {
		const response = await client.videos[":videoId"].upload.$post({ param: { videoId } });
		const data = await responseHandler(response);
		const endpoint = data.uploadUrl;
		if (!endpoint) throw new Error("videos.upload_failed");

		await new Promise<void>((resolve, reject) => {
			const upload = createUpload({
				endpoint,
				// A recording arrives as a bare blob and is named here; a file the author chose keeps
				// its own name and type, which is what Mux reads the container from.
				file:
					blob instanceof File ? blob : new File([blob], `${videoId}.webm`, { type: "video/webm" }),
				dynamicChunkSize: true,
			});
			upload.on("progress", (event) => onProgress(event.detail / 100));
			upload.on("success", () => resolve());
			upload.on("error", () => reject(new Error("videos.upload_failed")));
		});
	};
}

/**
 * Everything that has to be true before a capture starts, so the author can walk away from the
 * page they started on.
 *
 * The block is inserted and the page saved *first*, deliberately. The wiki editor holds its
 * draft in a form with no persistence, so navigating away — the very thing this feature exists
 * to allow — would otherwise discard whatever the author had already written. Anchoring the
 * placeholder before the capture also gives the finished recording somewhere to land: by the
 * time it stops, the editor and its cursor are long gone, but the block is part of the saved
 * page and turns into a player on its own.
 *
 * @param context - The page being edited, how to write the block into it, and how to save.
 * @returns The destination for the recorder, or null when nothing could be prepared — in which
 * case no capture is started at all.
 */
export async function prepareRecording(context: {
	spaceId: string;
	pageId: string;
	/** The page's own title, which is what the recording panel calls the take in progress. */
	pageTitle: string;
	/** `${orgId}:${userId}`, so a recording rescued from disk is offered back only to its author. */
	owner: string;
	insertBlock: (videoId: string) => boolean;
	save: () => Promise<boolean>;
	/** Puts the page back as it was, for every path that fails after the block was written. */
	rollback: () => Promise<void>;
}): Promise<RecordingTarget | null> {
	const response = await client.videos.index.$post({
		json: { wikiPageId: context.pageId },
	});
	const data = await responseHandler(response);
	const videoId = data.video.id;

	// Anything that goes wrong from here leaves a row and a block behind, so each exit undoes
	// both. A half-prepared recording is worse than none: the page keeps a spinner nothing will
	// ever resolve, and the row outlives it only long enough for the sweep to make it a dead end.
	const abandon = async () => {
		// The rollback touches the editor the author is looking at, and it is the half that can
		// fail. Its failure must not keep the row alive: a row whose block is gone is invisible
		// until the nightly sweep finds it a day later.
		try {
			await context.rollback();
		} catch (error) {
			console.error("Undoing the placeholder block failed:", error);
		}
		await client.videos[":videoId"].$delete({ param: { videoId } });
		return null;
	};

	if (!context.insertBlock(videoId)) {
		await client.videos[":videoId"].$delete({ param: { videoId } });
		return null;
	}
	if (!(await context.save())) return await abandon();

	return {
		videoId,
		label: context.pageTitle,
		href: `/wiki/${context.spaceId}/${context.pageId}`,
		owner: context.owner,
		upload: uploadRecording(videoId),
		abandon,
	};
}
